/**
 * The purchase order's Receipts tab: where each of its lines is going, line
 * by line — a quantity to one destination, a customer's address or
 * one of our named warehouses (order to invoice primitive 1) — what was
 * despatched to it (doc 102 §9.4), what arrived, when, and who recorded it. The order's own delivery lines are its
 * Delivery step's rows (`ChainPanels.tsx`), which share the row's step
 * (`RecordStep`), its tracking and the recording dialog from here.
 *
 * Record delivered and Record received sit on the row they complete
 * (docs/features/journey-visual-language/01_proposal.md §4). The row offers
 * the step only when the view carries its URL; Record received, until stock
 * control exists, is replaced by the backend's reason behind an ⓘ. The page
 * opens `LineCompletionDialog` for the step.
 */
import { useState } from "react";
import { newUuid } from "@shared-core/domain/object-ui/command-run";
import type {
  DeliveryLineDespatchView,
  DeliveryLineView,
} from "@shared-core/data/order-to-invoice-types";
import {
  deliveryDestinationLabel,
  deliveryDestinationShort,
  deliveryLineProgress,
  despatchCarrierAndTracking,
  latestDespatch,
  londonTodayIso,
  type OrderStepFailure,
} from "@shared-core/domain/order-to-invoice-presentation";
import { formatShortDateOrDash } from "@shared-core/domain/formatters";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../primitives/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../primitives/tooltip";
import { InfoTip } from "../../patterns/InfoTip";
import { StatusPill } from "./OrderPanels";

/** Some or all of one line, delivered (customer) or received into stock (warehouse). */
export interface LineCompletion {
  quantity: number;
  /** The delivered-on or received-on date. */
  on: string;
  /** The attempt's id (`request_id`), made when the dialog opens: a retry of the same submission records once. */
  requestId: string;
}

/** One row: the delivery line, and its purchase order line when the table spans several. */
export interface DeliveryLineRow {
  line: DeliveryLineView;
  /** "Line 2"; omitted when the purchase order has one line. */
  ownerLabel?: string;
}

/** The step a delivery line offers now, if any: its words follow where it goes. */
function recordLabel(line: DeliveryLineView): string {
  return line.destination.kind === "warehouse"
    ? "Record received"
    : "Record delivered";
}

function isRecordable(line: DeliveryLineView): boolean {
  return !!(line.record_delivered_url || line.record_received_url);
}

/** Where it is going, short; the full address on hover or focus. */
function ShortDestination({ line }: { line: DeliveryLineView }) {
  const d = line.destination;
  // Into stock, or one of our own sites for internal use (the mailing house).
  const badge =
    d.kind === "warehouse"
      ? d.operated_by_name === null && !d.stock_item_ref
        ? "Internal"
        : "Stock"
      : null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          className="inline-flex items-center gap-1.5 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {badge && <Badge variant="outline">{badge}</Badge>}
          <span className="underline decoration-dotted decoration-muted-foreground/50 underline-offset-2">
            {deliveryDestinationShort(d)}
          </span>
          <span className="sr-only">: {deliveryDestinationLabel(d)}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">{deliveryDestinationLabel(d)}</TooltipContent>
    </Tooltip>
  );
}

/** A despatch's carrier and tracking number, the number linked to the carrier's tracking page when there is one. */
function DespatchTracking({
  despatch,
}: {
  despatch: DeliveryLineDespatchView;
}) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {despatch.carrier_label}
      {despatch.tracking_number &&
        (despatch.tracking_url ? (
          <a
            href={despatch.tracking_url}
            target="_blank"
            rel="noreferrer"
            className="tabular-nums underline"
          >
            {despatch.tracking_number}
          </a>
        ) : (
          <span className="text-muted-foreground tabular-nums">
            {despatch.tracking_number}
          </span>
        ))}
    </span>
  );
}

/** "1,000 · 14 Oct · DX 1234567891": what went to this delivery line, its latest despatch's date and tracking. */
export function Despatched({ line }: { line: DeliveryLineView }) {
  const latest = latestDespatch(line);
  if (!latest || !line.despatched_quantity)
    return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1">
      <span className="tabular-nums">
        {line.despatched_quantity.toLocaleString("en-GB")} ·{" "}
        {formatShortDateOrDash(latest.despatched_on)}
      </span>
      {despatchCarrierAndTracking(latest) && (
        <>
          <span>·</span>
          <DespatchTracking despatch={latest} />
        </>
      )}
    </span>
  );
}

/** The carrier and its reference: the latest despatch's, its number linked to the carrier's tracking page. */
export function Tracking({ line }: { line: DeliveryLineView }) {
  const latest = latestDespatch(line);
  if (latest && despatchCarrierAndTracking(latest))
    return <DespatchTracking despatch={latest} />;
  const t = line.tracking;
  if (!t || (!t.carrier && !t.reference))
    return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {t.carrier}
      {t.reference && (
        <span className="text-muted-foreground tabular-nums">
          {t.reference}
        </span>
      )}
    </span>
  );
}

/** A delivery line's step, Record delivered or Record received: one outlined button, when the view offers it. */
export function RecordStep({
  line,
  busy,
  onRequestRecord,
}: {
  line: DeliveryLineView;
  busy: boolean;
  onRequestRecord: (line: DeliveryLineView) => void;
}) {
  if (!isRecordable(line)) return null;
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      disabled={busy}
      onClick={() => onRequestRecord(line)}
    >
      {recordLabel(line)}
    </Button>
  );
}

/** The row's step: its button, or why it can't be taken here yet. */
function NextStep({
  line,
  busy,
  onRequestRecord,
}: {
  line: DeliveryLineView;
  busy: boolean;
  onRequestRecord: (line: DeliveryLineView) => void;
}) {
  if (isRecordable(line)) {
    return (
      <RecordStep line={line} busy={busy} onRequestRecord={onRequestRecord} />
    );
  }
  if (line.record_received_blocked_reason) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        Record received
        <InfoTip label="Why Record received isn't offered">
          {line.record_received_blocked_reason}
        </InfoTip>
      </span>
    );
  }
  return null;
}

/** The purchase order's Receipts: what arrived, when, and who recorded it. */
export function DeliveryLinesTable({
  rows,
  busy = false,
  onRequestRecord,
}: {
  rows: DeliveryLineRow[];
  busy?: boolean;
  /** Open the row's step; without it the table is read only. */
  onRequestRecord?: (line: DeliveryLineView) => void;
}) {
  const hasOwner = rows.some((r) => r.ownerLabel);
  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Nothing to deliver on this purchase order yet.
      </p>
    );
  }
  return (
    <Table className="text-sm">
      <TableHeader>
        <TableRow>
          {hasOwner && <TableHead>Line</TableHead>}
          <TableHead className="text-right">Qty</TableHead>
          <TableHead>Going to</TableHead>
          <TableHead>Despatched</TableHead>
          <TableHead>Arrived</TableHead>
          <TableHead>Recorded by</TableHead>
          <TableHead>Status</TableHead>
          {onRequestRecord && (
            <TableHead>
              <span className="sr-only">Next step</span>
            </TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map(({ line, ownerLabel }) => {
          const progress = deliveryLineProgress(line);
          return (
            <TableRow key={line.id}>
              {hasOwner && (
                <TableCell className="tabular-nums text-muted-foreground">
                  {ownerLabel}
                </TableCell>
              )}
              <TableCell className="text-right tabular-nums">
                {line.quantity.toLocaleString("en-GB")}
              </TableCell>
              <TableCell>
                <ShortDestination line={line} />
              </TableCell>
              <TableCell className="whitespace-normal">
                <Despatched line={line} />
              </TableCell>
              <TableCell className="tabular-nums">
                {line.completed_quantity > 0 && line.status_on
                  ? formatShortDateOrDash(line.status_on)
                  : "—"}
              </TableCell>
              <TableCell>{line.completed_by ?? "—"}</TableCell>
              <TableCell className="whitespace-normal">
                <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <StatusPill
                    tone={line.outstanding_quantity === 0 ? "done" : "neutral"}
                  >
                    {line.status_label}
                    {progress && ` · ${progress}`}
                  </StatusPill>
                  {line.status_on && (
                    <span className="text-xs tabular-nums text-muted-foreground">
                      {formatShortDateOrDash(line.status_on)}
                    </span>
                  )}
                </span>
              </TableCell>
              {onRequestRecord && (
                <TableCell className="text-right">
                  <NextStep
                    line={line}
                    busy={busy}
                    onRequestRecord={onRequestRecord}
                  />
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/** A field's message beside its input, wired for assistive tech. */
function FieldMessage({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <p id={id} className="text-sm text-red-600">
      {message}
    </p>
  );
}

/**
 * Record delivered, or Record received: a quantity (the line's outstanding,
 * by default) and a date (today, by default), each checked beside its field.
 * A part leaves the rest outstanding. The dialog makes one idempotency key
 * when it opens and resends it on a retry, so a retry records once.
 * `onRecord` resolves to the refusal to show, or null once recorded.
 */
export function LineCompletionDialog({
  line,
  busy,
  onClose,
  onRecord,
}: {
  line: DeliveryLineView;
  busy: boolean;
  onClose: () => void;
  onRecord: (completion: LineCompletion) => Promise<OrderStepFailure | null>;
}) {
  const toWarehouse = line.destination.kind === "warehouse";
  const outstanding = line.outstanding_quantity;
  const today = londonTodayIso();
  const [requestId] = useState(newUuid);
  const [quantity, setQuantity] = useState(String(outstanding));
  const [on, setOn] = useState(today);
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);

  // Checked as typed, beside each field; the server's refusal replaces these on submit.
  const parsed = Number(quantity);
  const quantityProblem =
    quantity.trim() === "" || !Number.isInteger(parsed) || parsed <= 0
      ? "Enter a whole number above 0."
      : parsed > outstanding
        ? `Only ${outstanding.toLocaleString("en-GB")} are outstanding on this line.`
        : undefined;
  const dateProblem = !on
    ? "Enter a date."
    : on > today
      ? "The date can't be in the future."
      : undefined;
  const quantityError = quantityProblem ?? failure?.fieldErrors.quantity;
  const dateError =
    dateProblem ??
    failure?.fieldErrors[toWarehouse ? "received_on" : "delivered_on"];
  const remainder =
    !quantityProblem && parsed < outstanding ? outstanding - parsed : 0;
  const title = recordLabel(line);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {deliveryDestinationLabel(line.destination)}.{" "}
            {toWarehouse
              ? "Goods in, against the purchase order line."
              : "From the supplier's tracking or the customer's confirmation."}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setFailure(null);
            if (quantityProblem || dateProblem) return;
            const refused = await onRecord({ quantity: parsed, on, requestId });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          {failure && (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{failure.summary}</AlertDescription>
            </Alert>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="line-completion-quantity">
                {toWarehouse ? "Quantity received" : "Quantity delivered"}
              </Label>
              <Input
                id="line-completion-quantity"
                inputMode="numeric"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                aria-invalid={quantityError ? true : undefined}
                aria-describedby={
                  quantityError ? "line-completion-quantity-error" : undefined
                }
              />
              <FieldMessage
                id="line-completion-quantity-error"
                message={quantityError}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="line-completion-date">
                {toWarehouse ? "Received on" : "Delivered on"}
              </Label>
              <Input
                id="line-completion-date"
                type="date"
                value={on}
                max={today}
                onChange={(e) => setOn(e.target.value)}
                aria-invalid={dateError ? true : undefined}
                aria-describedby={
                  dateError ? "line-completion-date-error" : undefined
                }
              />
              <FieldMessage
                id="line-completion-date-error"
                message={dateError}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {outstanding.toLocaleString("en-GB")} outstanding on this line.
            {remainder > 0 &&
              ` ${remainder.toLocaleString("en-GB")} will stay outstanding.`}
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy || !!quantityProblem || !!dateProblem}
            >
              {title}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
